import styled from 'styled-components'

interface TextProps {
  fontSize?: string
  fontColor?: string
  fontWeight?: string
}

interface DetailProps {
  gap?: string
}

interface GraphProps {
  height?: string
  width?: string
}

interface DetailWrapperProps {
  $direction?: 'row' | 'column'
}

interface TextWrapperProps {
  $align?: 'flex-start' | 'center' | 'flex-end'
}

export const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 32px;
  align-items: stretch;
  min-height: 0;
  height: 100%;
  overflow: visible;
`

export const ContentWrapper = styled.div<{ $tabsAttached?: boolean }>`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 8px;
  box-shadow: ${(props) =>
    props.$tabsAttached ? 'none' : '0px 4px 16px 0px #00000029'};
  overflow: ${(props) => (props.$tabsAttached ? 'visible' : 'hidden')};

  ${(props) =>
    props.$tabsAttached &&
    `
    z-index: 0;

    &::after {
      position: absolute;
      inset: 0;
      z-index: -1;
      border-radius: 8px;
      box-shadow: 0px 4px 16px 0px #00000029;
      clip-path: inset(72px -24px -24px -24px);
      content: '';
      pointer-events: none;
    }

    > div:last-child {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  `}
`

export const BodyWrapper = styled.div`
  flex: 1;
  min-height: 0;
  padding: 24px;
  background: #ffffff;
  overflow-y: auto;
`

export const Header = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 1;
`

export const AnalysisWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 24px;
`

export const AnalysisGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: minmax(280px, 0.9fr) minmax(420px, 1.4fr);
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

export const AnalysisPanel = styled.div`
  display: flex;
  width: 100%;
  padding: 16px;
  flex-direction: column;
  gap: 12px;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--gray10, #fff);
`

export const AnalysisPanelTitle = styled.div`
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`

export const AnalysisConditionGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const AnalysisConditionItem = styled.div`
  display: flex;
  min-height: 62px;
  padding: 10px 12px;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  border-radius: 4px;
  background: var(--bluegray10, #f4f5f8);
`

export const AnalysisConditionLabel = styled.div`
  color: var(--gray70, #808080);
  font-family: SUIT;
  font-size: 11px;
  font-weight: 600;
`

export const AnalysisConditionValue = styled.div`
  min-width: 0;
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
`

export const Body = styled.div`
  display: flex;
  gap: 40px;
`

export const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 50%;
`

export const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 50%;
`

export const SectionFrame = styled.section`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
`

export const SectionHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;

  &::before,
  &::after {
    content: '';
    height: 1px;
    flex: 1;
    background: var(--gray30, #e6e6e6);
  }
`

export const SectionMark = styled.div`
  display: none;
`

export const SectionTitle = styled.div`
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
`

export const CollapsibleSectionTitle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
`

export const SectionTitleToggleText = styled.span`
  color: var(--gray70, #808080);
  font-size: 11px;
  font-weight: 600;
`

export const SectionTitleChevron = styled.span<{ $collapsed?: boolean }>`
  width: 7px;
  height: 7px;
  border-right: 2px solid var(--gray70, #808080);
  border-bottom: 2px solid var(--gray70, #808080);
  transform: ${(props) =>
    props.$collapsed
      ? 'translateY(-2px) rotate(45deg)'
      : 'translateY(2px) rotate(225deg)'};
  transition: transform 0.2s ease;
`

export const SectionDivider = styled.div`
  display: none;
`

export const DetailFrame = styled.div<DetailProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(props) => (props.gap ? props.gap : '16px')};
  align-self: stretch;
`
export const DetailWrapper = styled.div<DetailWrapperProps>`
  display: flex;
  width: 100%;
  flex-direction: ${(props) => props.$direction || 'row'};
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`
export const DetailColWrapper = styled.div`
  display: flex;
  width: 100%;
  padding-left: 10px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

export const ToggleHeader = styled.button<{ $open?: boolean }>`
  display: flex;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid var(--gray30, #e6e6e6);
  border-radius: ${(props) => (props.$open ? '4px 4px 0 0' : '4px')};
  background: rgba(87, 201, 167, 0.08);
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`

export const ToggleIcon = styled.span<{ $open?: boolean }>`
  color: #57c9a7;
  font-size: 14px;
  line-height: 1;
  transform: ${(props) => (props.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
`

export const ToggleContent = styled.div`
  display: flex;
  width: 100%;
  padding: 12px;
  flex-direction: column;
  gap: 10px;
  border: 1px solid var(--gray30, #e6e6e6);
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: #fff;
`

export const ProfitShareSummaryGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const ProfitShareSummaryCard = styled.div`
  display: flex;
  min-width: 0;
  min-height: 88px;
  padding: 12px;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--bluegray10, #f4f5f8);
`

export const ProfitShareSummaryLabel = styled.div`
  color: var(--gray70, #808080);
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

export const ProfitShareSummaryValue = styled.div<{ $negative?: boolean }>`
  color: ${(props) => (props.$negative ? '#d32f2f' : '#2f3c5e')};
  font-family: SUIT;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  white-space: nowrap;
`

export const ProfitShareSummarySubText = styled.div`
  color: var(--gray70, #808080);
  font-family: SUIT;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
`

export const ProfitShareRateControl = styled.div`
  display: flex;
  min-width: 260px;
  padding: 14px 16px;
  align-items: center;
  gap: 14px;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--green10, #f4fcfa);
`

export const ProfitShareRateLabel = styled.label`
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
`

export const ProfitShareRateInputWrapper = styled.div`
  display: flex;
  height: 38px;
  align-items: center;
  gap: 4px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid var(--green100, #57c9a7);
  background: #fff;
`

export const ProfitShareRateInput = styled.input`
  width: 56px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--gray90, #333);
  font-family: SUIT;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-align: right;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

export const ProfitShareRateUnit = styled.span`
  color: var(--gray90, #333);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`

export const ProfitShareRateMeta = styled.div`
  color: var(--gray70, #808080);
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
`

export const FeasibilitySummaryGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const FeasibilitySummaryCard = styled.div`
  display: flex;
  min-width: 0;
  min-height: 86px;
  padding: 12px;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  border-radius: 4px;
  border: 1px solid rgba(87, 201, 167, 0.35);
  background: rgba(87, 201, 167, 0.08);
`

export const FeasibilitySummaryLabel = styled.div`
  color: var(--gray70, #808080);
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 1.35;
`

export const FeasibilitySummaryValue = styled.div<{
  $accent?: boolean
  $danger?: boolean
  $lower?: boolean
}>`
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: 2px;
  margin-top: ${(props) => (props.$lower ? '10px' : '0')};
  color: ${(props) =>
    props.$danger ? '#d32f2f' : props.$accent ? '#147a5b' : '#2f3c5e'};
  font-family: SUIT;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  white-space: nowrap;
`

export const FeasibilitySummarySuffix = styled.span`
  color: inherit;
  font-family: SUIT;
  font-size: 11px;
  font-weight: 500;
`

export const FeasibilityLoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 12px;
  font-weight: 700;
`

export const FeasibilitySpinner = styled.div`
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  border-radius: 50%;
  border: 2px solid rgba(87, 201, 167, 0.24);
  border-top-color: #57c9a7;
  animation: sell-plan-feasibility-spin 0.8s linear infinite;

  @keyframes sell-plan-feasibility-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const FeasibilityBarChart = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 4px;
  border: 1px solid rgba(47, 60, 94, 0.12);
  background: #ffffff;
`

export const FeasibilityBarChartHeader = styled.div`
  display: flex;
  align-items: center;
  color: #2f3c5e;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 700;
`

export const FeasibilityCurrentLegend = styled.div<{ $left: number }>`
  position: relative;
  width: 100%;
  height: 18px;
  margin-top: -2px;
  margin-bottom: -4px;

  span {
    position: absolute;
    left: ${(props) => props.$left}%;
    transform: translateX(-50%);
    color: #2f3c5e;
    font-family: SUIT;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }
`

export const FeasibilityBarGuideRow = styled.div`
  display: grid;
  grid-template-columns: 128px minmax(120px, 1fr) 118px;
  align-items: center;
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`

export const FeasibilityCurrentLegendSpacer = styled.div`
  min-width: 0;
`

export const FeasibilityBarRow = styled.div`
  display: grid;
  grid-template-columns: 128px minmax(120px, 1fr) 118px;
  align-items: center;
  gap: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`

export const FeasibilityBarLabel = styled.div`
  color: #2f3c5e;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
`

export const FeasibilityBarTrack = styled.div`
  position: relative;
  height: 22px;
  overflow: hidden;
  border-radius: 4px;
  background: #f1f4f8;
`

export const FeasibilityBarFill = styled.div<{
  $danger?: boolean
  $left: number
  $width: number
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.$left}%;
  width: ${(props) => props.$width}%;
  height: 100%;
  border-radius: 4px;
  background: ${(props) =>
    props.$danger ? '#d32f2f' : 'var(--green100, #57c9a7)'};
`

export const FeasibilityCurrentMarker = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.$left}%;
  width: 2px;
  transform: translateX(-1px);
  background: #2f3c5e;

  &::after {
    position: absolute;
    top: -1px;
    left: 50%;
    width: 7px;
    height: 7px;
    transform: translate(-50%, -50%) rotate(45deg);
    background: #2f3c5e;
    content: '';
  }
`

export const FeasibilityLimitMarker = styled.div<{
  $danger?: boolean
  $left: number
}>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: ${(props) => props.$left}%;
  width: 2px;
  transform: translateX(-1px);
  border-radius: 99px;
  background: ${(props) => (props.$danger ? '#d32f2f' : '#147a5b')};
`

export const FeasibilityBarValue = styled.div<{ $danger?: boolean }>`
  color: ${(props) => (props.$danger ? '#d32f2f' : '#147a5b')};
  font-family: SUIT;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 900px) {
    text-align: left;
  }
`

export const OptionListWrapper = styled.div`
  display: flex;
  align-content: flex-end;
  gap: 16px;
  align-self: stretch;
  flex-wrap: wrap;
`

export const PatternSelectWrapper = styled.div`
  display: flex;
  width: fit-content;
  max-width: 100%;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--gray10, #fff);
`

export const Line = styled.div`
  width: 100%;
  height: 1px;
  background: var(--gray50, #b3b3b3);
`

export const TextWrapper = styled.div<TextWrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => props.$align || 'flex-start'};
  gap: 4px;
`
export const TextBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`
export const TextBox2 = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding-top: 1px;
`

export const Text = styled.div<TextProps>`
  color: ${(props) => (props.fontColor ? props.fontColor : '#808080')};
  font-family: SUIT;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '11px')};
  font-style: normal;
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '600')};
  line-height: normal;
`

export const GraphWrapper = styled.div<GraphProps>`
  display: flex;
  width: ${(props) => (props.width ? props.width : '421px')};
  height: ${(props) => (props.height ? props.height : '100%')};
  padding: 12px;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1 0 0;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--gray10, #fff);
`

export const GraphBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`
export const SeasonFrame = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`
export const SeasonBox = styled.div`
  display: flex;
  padding: 6px 10px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 2px;
  flex: 1 0 0;
  border-radius: 4px;
  background: var(--bluegray10, #f4f5f8);
`
export const SeasonTextBox = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

export const Table = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  border-radius: 4px;
  border: 1px solid transparent;
  border-collapse: collapse;
`

export const TableRow = styled.div`
  display: flex;
`

interface TableGrayProps {
  white?: boolean
}

export const TableCell = styled.div<TableGrayProps>`
  display: flex;
  width: 100%;
  height: 38px;
  padding: 8px 0px 8px 0px;
  border: 0.5px solid #e6e6e6;
  background: ${(props) => (props.white ? '' : '#f4f5f8')};

  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0em;
  align-items: center;
  justify-content: center;
`
export const TableCell2 = styled.div<TableGrayProps>`
  display: flex;
  width: 100%;
  height: 46px;
  padding: 8px 0px 8px 0px;
  border: 0.5px solid #e6e6e6;
  background: ${(props) => (props.white ? '' : '#f4f5f8')};

  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0em;
  align-items: center;
  justify-content: center;
`

export const ChargeFrame = styled.div`
  display: flex;
  height: 100%;
  padding: 0px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--gray10, #fff);
`
export const ChargeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  padding: 10px;
`

export const InUnitContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const InUnitItem = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

export const InUnitLabel = styled.div`
  font-family: SUIT;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0em;
  text-align: left;
  color: #2f3c5e;
  white-space: nowrap;
  padding-top: 5px;
`

export const Input = styled.input`
  border: none;
  width: 50px;
  height: 100%;
  background: transparent;
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px #cfe8ff;
  text-align: center;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0em;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background-color: #f0f8ff;
    cursor: text;
  }

  &:focus {
    outline: none;
    background-color: #e3f2fd;
    box-shadow: inset 0 0 0 1px #b7ddff;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

export const MonthChipWrapper = styled.div`
  display: flex;
  gap: 4px;
`

export const MonthChip = styled.div<{ selected: boolean }>`
  display: flex;
  min-width: 32px;
  height: 18px;
  padding: 0 6px;
  border-radius: 99px;
  background: #57c9a7;
  border: 1px solid #0000001a;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${({ selected }) =>
    selected ? 'rgba(87, 201, 167, 1)' : 'rgba(87, 201, 167, 0.1)'};
`

export const MonthChipText = styled.div`
  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  color: #ffffff;
  color: #333333;
  user-select: none;
  white-space: nowrap;
`

export const InputItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const InputLabel = styled.div`
  font-family: SUIT;
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  letter-spacing: 0em;
  text-align: left;
  color: #2f3c5e;
`

interface TableGrayProps {
  white?: boolean
}

export const OptionFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`

export const OptionDetailWrapper = styled.div`
  display: flex;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--gray10, #fff);
`

export const OptionDetailBox = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  gap: 4px;
`
export const Rectangle = styled.div`
  width: 4px;
  height: 14px;
  border-radius: 99px;
  background: var(--green100, #57c9a7);
`
export const OptionTextBox = styled.div`
  padding-top: 1px;
  width: max-content;
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
`

export const PlanDefaultSummaryBox = styled.div`
  display: flex;
  width: 100%;
  padding: 12px;
  align-items: stretch;
  gap: 12px;
  align-self: stretch;
  flex-wrap: wrap;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--bluegray10, #f4f5f8);
`

export const PlanDefaultSummaryItem = styled.div`
  display: flex;
  min-width: 130px;
  flex: 1 1 130px;
  flex-direction: column;
  gap: 4px;
`

export const PlanDefaultSummaryLabel = styled.div`
  color: var(--gray70, #808080);
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

export const PlanDefaultSummaryValue = styled.div`
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`

export const SubText = styled.div`
  color: var(--gray90, #333);
  text-align: center;
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`
export const SubTextUnit = styled.div`
  color: var(--gray90, #333);
  font-family: SUIT;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

export const DetailDescriptionText = styled.div`
  padding-left: 10px;
  color: var(--gray70, #808080);
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`
export const TableTitleFrame = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
export const CustomerNameWrapper = styled.div`
  display: flex;
  padding: 6px 8px;
  align-items: center;
  border-radius: 4px 4px 0px 0px;
  background: var(--green100, #57c9a7);
`

export const CustomerName = styled.div`
  color: var(--gray10, #fff);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

export const PlanName = styled.div`
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`
